import type { Patch } from "@freightsimple/shared/types";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { NmfcSubItemChoice } from "@freightsimple/generated-dashboard-openapi-client/models/NmfcSubItemChoice";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import LineItemsNmfcInput from "../Inputs/LineItemsNmfcInput";

interface LineItemsFreightClassFieldsProps {
  lineItem: AddLineItemType;
  onNmfcChange: (value: Patch<LineItem>) => void;
  nmfcLookupInProgress: boolean;
  nmfcDescription: string | undefined;
  nmfcErrorMessage: ErrorMessageType;
  clearNmfcError: () => void;
  nmfcSubItems: NmfcSubItemChoice[] | undefined;
  forceValidation: boolean;
  freightClassErrorMessage: ErrorMessageType;
}

/**
 * The freight-class half of the shipment-contents section: the optional NMFC
 * code, and the error shown when we couldn't work the class out.
 *
 * Only rendered for shipments where the class actually matters, which is why it
 * lives behind its own flag rather than always being on screen.
 *
 * Returns a fragment, not a Stack — these sit directly in the contents
 * section's Stack and take its spacing.
 */
export function LineItemsFreightClassFields(
  props: LineItemsFreightClassFieldsProps,
) {
  const { lineItem } = props;

  return (
    <>
      <LineItemsNmfcInput
        onChange={function (newNmfcNumber: string | undefined) {
          props.onNmfcChange({
            ...lineItem,
            nmfcItemNumber: newNmfcNumber,
          });
        }}
        nmfcCode={lineItem.nmfcItemNumber}
        loading={props.nmfcLookupInProgress}
        description={props.nmfcDescription}
        confirmed={lineItem.nmfcItemNumber !== undefined}
        errorMessage={props.nmfcErrorMessage}
        clearError={props.clearNmfcError}
        subItems={props.nmfcSubItems}
        freightClass={lineItem.freightClass}
      />
      {/* Freight class is derived from weight and dimensions (or the NMFC
          code), not entered directly, so no input owns this error. It blocks
          booking, so it must be shown here — otherwise the user only sees the
          generic "correct the above errors" banner. Skipped while an NMFC error
          is visible on the same field. */}
      {props.forceValidation &&
        props.nmfcErrorMessage === undefined &&
        props.freightClassErrorMessage && (
          <T size="sm" color="red">
            Freight class is required for this shipment. Enter the weight and
            dimensions so it can be calculated, or provide an NMFC code.
          </T>
        )}
    </>
  );
}
